import React, { useEffect, useRef, useState } from 'react';
import './Spinner.css';
import { observer } from 'mobx-react-lite';
import etaLoader from '@/lib/EtaLoader';

const Spinner = observer(() => {
  const FINISH_DELAY = 2000; 
  const [visible, setVisible] = useState(false);
  const [displayedPercent, setDisplayedPercent] = useState(0);
  const hideTimeout = useRef<NodeJS.Timeout | null>(null);

  const size = 200;
  const strokeWidth = 2.5; 
  const center = size / 2;
  const radius = (size - 10) / 2; 
  const circumference = 2 * Math.PI * radius;

  // Sync displayed percent and prevent jumping backward
  useEffect(() => {
    const newVal = etaLoader.animatedPercentValue;
    if (newVal > displayedPercent) {
      setDisplayedPercent(newVal);
    }
  }, [etaLoader.animatedPercentValue]);

  // Handle Visibility Logic
  useEffect(() => {
    const isActive = etaLoader.percent < 100 && etaLoader.message;

    if (isActive) {
      // If loading starts/continues, show spinner and clear any pending hide timeouts
      setVisible(true);
      if (hideTimeout.current) {
        clearTimeout(hideTimeout.current);
        hideTimeout.current = null;
      }
    } else if (etaLoader.percent >= 100 && visible) {
      // If we hit 100 and it's currently visible, start the countdown to hide
      if (!hideTimeout.current) {
        hideTimeout.current = setTimeout(() => {
          setVisible(false);
          setDisplayedPercent(0); 
          hideTimeout.current = null;
        }, FINISH_DELAY);
      }
    }
  }, [etaLoader.percent, etaLoader.message, visible]); // Removed message dependency from inner logic check

  const IS_SPINNER_DISPLAY_FORCED = false; // Set to true to always show spinner for testing

  if (!visible && !IS_SPINNER_DISPLAY_FORCED) return null;

  const offset = circumference - (displayedPercent / 100) * circumference;

  return (
    <div className='spinner-overlay spinner'>
      <div className='spinner-content'>
        <svg width={size} height={size} className="progress-ring">
          <circle
            className="progress-ring__background"
            stroke="#e2e8f0"
            strokeWidth="1"
            fill="transparent"
            r={radius}
            cx={center}
            cy={center}
          />
          <circle
            className="progress-ring__bar"
            stroke="#000000"
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            style={{ strokeDashoffset: offset }}
            strokeLinecap="round"
            fill="transparent"
            r={radius}
            cx={center}
            cy={center}
          />
          <text
            x="50%"
            y="50%"
            dy=".25em"
            textAnchor="middle"
            className="progress-text"
          >
            {Math.round(displayedPercent)}%
          </text>
        </svg>
        <div className="spinner-message">{etaLoader.message}</div>
      </div>
    </div>
  );
});

export default Spinner;